import type { Trait } from './types.js';

/**
 * Catalogo de traits do protocolo v1.0.
 * Transcricao literal da Ficha de Campo v1.0 (docs/origem/ficha-de-campo-v1.0.pdf),
 * Estacao 3 (folhas 3-4) e Guia Rapido de Campo (folha 7).
 *
 * =========================================================================
 * ATENCAO - FAIXAS PLAUSIVEIS SAO PROVISORIAS
 * Os valores minPlausible/maxPlausible foram estimados para o Mangalarga
 * Marchador adulto e servem apenas para pegar erro de digitacao. Precisam da
 * revisao do Rafael antes da primeira coleta real, e devem ser reaferidos
 * contra a distribuicao observada depois dos primeiros ~100 animais.
 * Faixa larga demais nao pega erro; estreita demais treina a equipe a
 * ignorar aviso, que e pior.
 * =========================================================================
 *
 * REGRA: `code` e IMUTAVEL. Nunca reaproveitar um codigo com significado novo.
 */

let order = 0;
const next = () => ++order;

// --- Alturas (hipometro) --------------------------------------------------

const alturas: Trait[] = [
  {
    code: 'A_CER', kind: 'linear', group: 'altura', label: 'Altura de cernelha',
    definition: 'Solo ate o ponto mais alto da cernelha.',
    unit: 'cm', decimals: 1, instrument: 'hipometro', sided: false,
    levelMin: 1, minPlausible: 120, maxPlausible: 180, sortOrder: next(),
  },
  {
    code: 'A_GAR', kind: 'linear', group: 'altura', label: 'Altura de garupa',
    definition: 'Solo ate o ponto mais alto da garupa (tuberosidade sacral).',
    unit: 'cm', decimals: 1, instrument: 'hipometro', sided: false,
    levelMin: 1, minPlausible: 120, maxPlausible: 180, sortOrder: next(),
  },
  {
    code: 'A_BPE', kind: 'linear', group: 'altura', label: 'Altura na base do pescoco',
    definition: 'Solo ate o ponto mais baixo da linha superior entre pescoco e cernelha.',
    unit: 'cm', decimals: 1, instrument: 'hipometro', sided: false,
    levelMin: 2, minPlausible: 90, maxPlausible: 165, sortOrder: next(),
  },
  {
    code: 'P_TOR', kind: 'linear', group: 'altura', label: 'Profundidade toracica',
    definition: 'Cernelha ate o esterno, na regiao da cilha.',
    unit: 'cm', decimals: 1, instrument: 'hipometro', sided: false,
    levelMin: 2, minPlausible: 50, maxPlausible: 90, sortOrder: next(),
  },
  {
    code: 'A_SUB', kind: 'linear', group: 'altura', label: 'Vazio subesternal',
    definition: 'Solo ate a borda ventral do torax, na regiao da cilha.',
    unit: 'cm', decimals: 1, instrument: 'hipometro', sided: false,
    levelMin: 2, minPlausible: 55, maxPlausible: 100, sortOrder: next(),
  },
];

// --- Comprimentos ---------------------------------------------------------

type LinearRow = [code: string, label: string, definition: string, instrument: Trait['instrument'], min: number, max: number];

const comprimentoRows: LinearRow[] = [
  ['C_CAB', 'Cabeca', 'Nuca ate a ponta do labio superior.', 'compasso', 45, 75],
  ['C_FRO', 'Fronte', 'Linha entre as bases das orelhas ate a linha entre os cantos internos dos olhos.', 'compasso', 10, 28],
  ['C_PES', 'Pescoco', 'Nuca ate a ponta da espadua, pelo bordo do pescoco.', 'fita', 50, 100],
  ['C_ESP', 'Espadua', 'Extremidade dorsal da espinha da escapula (M3) ate a ponta da espadua (M4).', 'compasso', 35, 68],
  ['C_BRA', 'Braco', 'Ponta da espadua (M4) ate o cotovelo (M5).', 'compasso', 20, 48],
  ['C_ANT', 'Antebraco', 'Cotovelo (M5) ate o carpo (M6).', 'compasso', 25, 55],
  ['C_CANA', 'Canela anterior', 'Carpo (M6) ate o boleto anterior (M7).', 'compasso', 15, 38],
  ['C_DOR', 'Dorso', 'Cernelha (M2) ate a ultima vertebra toracica (M9).', 'fita', 35, 75],
  ['C_LOM', 'Lombo', 'Ultima toracica (M9) ate a ponta da anca (M11).', 'fita', 15, 48],
  ['C_GAR', 'Garupa', 'Ponta da anca (M11) ate a ponta da nadega (M13).', 'compasso', 40, 72],
  ['C_FEM', 'Femoral', 'Trocanter maior (M12) ate o joelho (M14).', 'compasso', 30, 62],
  ['C_TIB', 'Tibial', 'Joelho (M14) ate a ponta do jarrete (M15).', 'compasso', 30, 62],
  ['C_CANP', 'Canela posterior', 'Ponta do jarrete (M15) ate o boleto posterior (M17).', 'compasso', 20, 48],
  ['C_COR', 'Comprimento do corpo', 'Ponta da espadua (M4) ate a ponta da nadega (M13). Hipometro ou compasso.', 'compasso', 125, 195],
];

const comprimentos: Trait[] = comprimentoRows.map(([code, label, definition, instrument, min, max]) => ({
  code, kind: 'linear', group: 'comprimento', label, definition,
  unit: 'cm', decimals: 1, instrument, sided: false, levelMin: 2,
  minPlausible: min, maxPlausible: max, sortOrder: next(),
}));

// --- Larguras (compasso) --------------------------------------------------

const larguraRows: Array<[string, string, string, number, number]> = [
  ['L_OLH', 'Distancia entre os olhos', 'Entre os cantos internos dos olhos.', 10, 28],
  ['L_CAB', 'Largura da cabeca', 'Maior largura, ao nivel das arcadas zigomaticas.', 15, 32],
  ['L_PEI', 'Largura do peito', 'Entre as faces externas das articulacoes escapulo-umerais.', 25, 58],
  ['L_ANC', 'Largura das ancas', 'Entre as tuberosidades coxais.', 35, 68],
  ['L_ISQ', 'Largura entre isquios', 'Entre as tuberosidades isquiaticas.', 15, 42],
];

const larguras: Trait[] = larguraRows.map(([code, label, definition, min, max]) => ({
  code, kind: 'linear', group: 'largura', label, definition,
  unit: 'cm', decimals: 1, instrument: 'compasso', sided: false, levelMin: 2,
  minPlausible: min, maxPlausible: max, sortOrder: next(),
}));

// --- Perimetros (fita) ----------------------------------------------------

const perimetroRows: Array<[string, string, string, 1 | 2 | 3, number, number]> = [
  ['PE_CAN', 'Perimetro de canela', 'Menor perimetro, terco medio do III metacarpiano.', 1, 15, 26],
  ['PE_CAR', 'Perimetro de carpo', 'Maior perimetro do carpo.', 2, 25, 42],
  ['PE_ANT', 'Perimetro de antebraco', 'Maior perimetro do terco proximal do antebraco.', 2, 30, 58],
  ['PE_TOR', 'Perimetro toracico', 'Logo atras da cernelha, passando pela cilha.', 1, 140, 215],
  ['PE_ABD', 'Perimetro abdominal', 'Maior circunferencia, regiao do flanco. Registrar a hora: o valor depende do enchimento gastrointestinal.', 2, 150, 245],
];

const perimetros: Trait[] = perimetroRows.map(([code, label, definition, levelMin, min, max]) => ({
  code, kind: 'perimetro', group: 'perimetro', label, definition,
  unit: 'cm', decimals: 1, instrument: 'fita', sided: false, levelMin,
  minPlausible: min, maxPlausible: max, sortOrder: next(),
}));

// --- Angulos (goniometro no campo / foto no escritorio) -------------------
// Pontos conforme folha 4 da ficha. HORIZONTAL = linha horizontal de referencia.

const anguloRows: Array<[string, string, string[], number, number]> = [
  ['AN_ESU', 'Escapulo-umeral', ['M3', 'M4', 'M5'], 90, 145],
  ['AN_COX', 'Coxofemoral', ['M11', 'M12', 'M14'], 90, 145],
  ['AN_IESP', 'Inclinacao da espadua', ['M3', 'M4', 'HORIZONTAL'], 35, 75],
  ['AN_IGAR', 'Inclinacao da garupa', ['M11', 'M13', 'HORIZONTAL'], 10, 50],
  ['AN_UMR', 'Umero-radial (cotovelo)', ['M4', 'M5', 'M6'], 105, 165],
  ['AN_FTI', 'Femorotibial', ['M12', 'M14', 'M16'], 105, 165],
  ['AN_JAR', 'Tibiotarsico (jarrete)', ['M14', 'M16', 'M17'], 125, 180],
  ['AN_QTA', 'Quartela anterior', ['M7', 'M8', 'HORIZONTAL'], 35, 75],
  ['AN_QTP', 'Quartela posterior', ['M17', 'M18', 'HORIZONTAL'], 35, 75],
  ['AN_CASA', 'Casco anterior', ['PAREDE_DORSAL', 'SOLO'], 35, 70],
  ['AN_CASP', 'Casco posterior', ['PAREDE_DORSAL', 'SOLO'], 40, 75],
];

const angulos: Trait[] = anguloRows.map(([code, label, points, min, max]) => ({
  code, kind: 'angulo', group: 'angulo', label,
  definition: `Vertice no ponto central. Pontos: ${points.join(' - ')}.`,
  unit: 'graus', decimals: 0, instrument: 'goniometro', sided: false, levelMin: 2,
  minPlausible: min, maxPlausible: max, points, sortOrder: next(),
}));

// --- Aprumos --------------------------------------------------------------
// Escala: -2 = desvio acentuado no sentido (-), 0 = correto, +2 = acentuado no sentido (+).
//
// NOTA DE PROTOCOLO: os itens de base de apoio recebem nota unica na ficha v1.0
// (`sided: false`); os demais sao anotados por lado. A propria ficha e ambigua
// nesse ponto (folha 5: "celula unificada" vs. "escrever o valor em cada lado").
// Precisa da decisao do Rafael antes da v1.1.

const aprumo = (
  code: string,
  group: Trait['group'],
  label: string,
  neg: string,
  pos: string,
  sided: boolean,
): Trait => ({
  code, kind: 'aprumo', group, label,
  definition: `Desvio negativo: ${neg}. Desvio positivo: ${pos}. Escala -2 a +2, sendo 0 correto.`,
  unit: 'escore', decimals: 0, sided, levelMin: 2,
  minPlausible: -2, maxPlausible: 2, sortOrder: next(),
});

const aprumos: Trait[] = [
  aprumo('AP_F_BASE', 'aprumo_frente', 'Base de apoio (frente)', 'estreita', 'larga', false),
  aprumo('AP_F_CARPO', 'aprumo_frente', 'Carpo (frente)', 'varo, joelhos abertos', 'valgo, joelhos fechados', true),
  aprumo('AP_F_PINCAS', 'aprumo_frente', 'Pincas (frente)', 'para dentro', 'para fora', true),
  aprumo('AP_PA_MEMBRO', 'aprumo_perfil_anterior', 'Posicao do membro anterior', 'acampado, a frente', 'remetido, sob si', true),
  aprumo('AP_PA_CARPO', 'aprumo_perfil_anterior', 'Carpo (perfil)', 'para tras', 'para frente', true),
  aprumo('AP_PA_QUART', 'aprumo_perfil_anterior', 'Quartela anterior (perfil)', 'baixa, mole', 'alta, reta', true),
  aprumo('AP_PP_MEMBRO', 'aprumo_perfil_posterior', 'Posicao do membro posterior', 'acampado, atras', 'remetido, sob si', true),
  aprumo('AP_PP_JARRETE', 'aprumo_perfil_posterior', 'Jarrete (perfil)', 'reto, aberto', 'curvo, fechado', true),
  aprumo('AP_PP_QUART', 'aprumo_perfil_posterior', 'Quartela posterior (perfil)', 'baixa, mole', 'alta, reta', true),
  aprumo('AP_T_BASE', 'aprumo_tras', 'Base de apoio (tras)', 'estreita', 'larga', false),
  aprumo('AP_T_JARRETES', 'aprumo_tras', 'Jarretes (tras)', 'fechados, de vaca', 'abertos', true),
  aprumo('AP_T_PINCAS', 'aprumo_tras', 'Pincas (tras)', 'para dentro', 'para fora', true),
];

export const traitsV1: Trait[] = [
  ...alturas,
  ...comprimentos,
  ...larguras,
  ...perimetros,
  ...angulos,
  ...aprumos,
];
